import type { Canvas as FabricCanvas } from 'fabric';
import type { ArtboardPrefs } from '../../../stores/editorPrefsStore';

export interface ArtboardOverlayContext {
  getPrefs: () => ArtboardPrefs;
  getRect: () => { left: number; top: number; width: number; height: number } | null;
  isSuppressed: () => boolean;
}

export function attachArtboardOverlay(
  canvas: FabricCanvas,
  ctx: ArtboardOverlayContext
): () => void {
  let disposed = false;

  const handler = () => {
    if (disposed) return;
    try {
      if (ctx.isSuppressed()) return;
      const prefs = ctx.getPrefs();
      if (!prefs.artboard_frame_enabled) return;

      const lower = canvas.getContext();
      if (!lower) return;
      const rect = ctx.getRect();
      if (!rect || !(rect.width > 0) || !(rect.height > 0)) return;

      const vt = canvas.viewportTransform;
      if (!vt || vt.length < 6) return;
      const zoom = Math.max(Number(canvas.getZoom?.() ?? vt[0] ?? 1), 0.0001);

      lower.save();
      lower.setTransform(vt[0], vt[1], vt[2], vt[3], vt[4], vt[5]);

      const stroke = prefs.artboard_stroke_color;
      const haloStroke = prefs.artboard_halo_color;
      const strokeW = Math.max(0.2, prefs.artboard_stroke_width) / zoom;
      const haloW = Math.max(0.1, prefs.artboard_halo_width) / zoom;

      lower.lineJoin = 'miter';
      lower.lineCap = 'butt';

      if (prefs.artboard_frame_style === 'dashed') {
        const dash = Math.max(1, prefs.artboard_dash_length) / zoom;
        lower.setLineDash([dash, dash]);
      } else {
        lower.setLineDash([]);
      }

      if (prefs.artboard_halo_enabled) {
        lower.strokeStyle = haloStroke;
        lower.lineWidth = strokeW + haloW * 2;
        lower.strokeRect(rect.left, rect.top, rect.width, rect.height);
      }

      lower.strokeStyle = stroke;
      lower.lineWidth = strokeW;
      lower.strokeRect(rect.left, rect.top, rect.width, rect.height);

      if (prefs.artboard_frame_style === 'double') {
        const gap = Math.max(2, strokeW * 2 + haloW * 2);
        lower.strokeRect(
          rect.left - gap,
          rect.top - gap,
          rect.width + gap * 2,
          rect.height + gap * 2
        );
      }

      lower.restore();
    } catch (err) {
      if (!disposed) {
        console.warn('[ArtboardOverlay] draw failed, disabling:', err);
        disposed = true;
      }
    }
  };

  canvas.on('after:render', handler);

  return () => {
    disposed = true;
    try {
      canvas.off('after:render', handler);
    } catch {
      /* canvas already disposed */
    }
  };
}

let suppressCount = 0;

export function isOverlaySuppressed(): boolean {
  return suppressCount > 0;
}

export function withOverlaySuppressed<T>(fn: () => T): T {
  suppressCount++;
  try {
    return fn();
  } finally {
    suppressCount = Math.max(0, suppressCount - 1);
  }
}

export async function withOverlaySuppressedAsync<T>(fn: () => Promise<T>): Promise<T> {
  suppressCount++;
  try {
    return await fn();
  } finally {
    suppressCount = Math.max(0, suppressCount - 1);
  }
}
